/**
 * SettingsTab Component
 * Organization settings within Admin page
 */

import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { clsx } from 'clsx';
import { toast } from 'react-hot-toast';
import { Save, Building2 } from 'lucide-react';
import { useOrganization } from '../../../hooks/useOrganization';
import { organizationService } from '../../../services';
import { Card, Button, Input, LoadingSpinner } from '../../../components/common';
import { PerformanceSettingsPanel } from '../../../components/performance/admin';
import { AIIntegrationCard } from '../../../components/admin/AIIntegrationCard';

export const SettingsTab: React.FC = () => {
  const { data: organization, isLoading: orgLoading, refetch } = useOrganization();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: '',
  });

  const [approvalEnabled, setApprovalEnabled] = useState(false);
  const [isTogglingApproval, setIsTogglingApproval] = useState(false);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        industry: organization.industry || '',
        size: organization.size || '',
      });
      const orgWithSettings = organization as { cycleSettings?: { requireOKRApproval?: boolean } };
      setApprovalEnabled(orgWithSettings.cycleSettings?.requireOKRApproval || false);
    }
  }, [organization]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setIsSaving(true);
    try {
      await organizationService.updateOrganization({
        name: formData.name,
        industry: formData.industry || undefined,
        size: formData.size || undefined,
      });
      toast.success('Settings saved successfully');
      setIsEditing(false);
      refetch();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleApproval = async () => {
    setIsTogglingApproval(true);
    try {
      await organizationService.updateCycleSettings({
        requireOKRApproval: !approvalEnabled,
      });
      setApprovalEnabled(!approvalEnabled);
      toast.success(
        !approvalEnabled
          ? 'OKR approval workflow enabled'
          : 'OKR approval workflow disabled'
      );
      refetch();
    } catch (error) {
      toast.error('Failed to update approval settings');
    } finally {
      setIsTogglingApproval(false);
    }
  };

  if (orgLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Organization Settings</h2>
          <p className="text-sm text-stone-500">
            Manage your organization's profile and preferences
          </p>
        </div>
      </div>

      {/* Organization Profile */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Organization Profile</h3>
              <p className="text-sm text-stone-500">Basic information about your organization</p>
            </div>
          </div>
          {!isEditing ? (
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <LoadingSpinner size="sm" /> : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Organization Name
            </label>
            {isEditing ? (
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter organization name"
              />
            ) : (
              <p className="text-stone-900">{organization?.name || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Industry
            </label>
            {isEditing ? (
              <select
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select industry</option>
                <option value="technology">Technology</option>
                <option value="finance">Finance</option>
                <option value="healthcare">Healthcare</option>
                <option value="education">Education</option>
                <option value="retail">Retail</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="consulting">Consulting</option>
                <option value="other">Other</option>
              </select>
            ) : (
              <p className="text-stone-900 capitalize">{organization?.industry || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Organization Size
            </label>
            {isEditing ? (
              <select
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1000+">1000+ employees</option>
              </select>
            ) : (
              <p className="text-stone-900">{organization?.size || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Plan
            </label>
            <p className="text-stone-900 capitalize">{organization?.plan || 'Trial'}</p>
          </div>
        </div>
      </Card>

      {/* OKR Settings */}
      <Card className="p-6">
        <h3 className="font-semibold text-stone-900 mb-6" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>OKR Settings</h3>

        <div className="space-y-6">
          {/* Approval Workflow Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-stone-900">Require OKR Approval</h4>
              <p className="text-sm text-stone-500">
                When enabled, OKRs must be approved by a manager before becoming active
              </p>
            </div>
            <Switch
              checked={approvalEnabled}
              onChange={handleToggleApproval}
              disabled={isTogglingApproval}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                approvalEnabled ? 'bg-primary-600' : 'bg-stone-200'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  approvalEnabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </Switch>
          </div>
        </div>
      </Card>

      {/* AI Integration */}
      <AIIntegrationCard organization={organization} />

      {/* Performance Score Weights */}
      <PerformanceSettingsPanel />

      {/* Subscription Info */}
      <Card className="p-6">
        <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Subscription</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-sm text-stone-500">Current Plan</p>
            <p className="font-medium text-stone-900 capitalize">{organization?.plan || 'Trial'}</p>
          </div>
          <div>
            <p className="text-sm text-stone-500">Status</p>
            <p className="font-medium text-stone-900 capitalize">{organization?.status || 'Active'}</p>
          </div>
          {organization?.trialEndsAt && (
            <div>
              <p className="text-sm text-stone-500">Trial Ends</p>
              <p className="font-medium text-stone-900">
                {new Date(organization.trialEndsAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
